/*
  Resource Manifest Redux-Sagas Setup
*/

import { takeLatest, takeEvery, select, call, put } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import { delay } from 'redux-saga';
import normalize from 'json-api-normalizer';

import { t } from 'services/translation';
import { convertDateTimezone } from 'ui_system/utilities/timezoneUtils';

import { fetchSlots } from 'data_system/resource_portal/actions/slotsActions';
import {
  createScheduleSelected,
  fetchExperienceAvailabilityStarted,
  fetchExperienceAvailabilityFailed,
  fetchExperienceAvailabilitySucceeded,
  showAvailabilityModal,
  saveSchedule,
  saveScheduleSucceeded,
  saveScheduleFailed,
} from 'data_system/resource_portal/actions/experienceActions';

import {
  getTourStepForm,
  getExperienceById,
  getSelectedDate,
  getSelectedTime,
  getSelectedExperienceId,
  getResourceTimezone,
  getExperienceSchedule,
} from 'data_system/resource_portal/selectors';

import { getUserId } from 'data_system/token_auth/selectors';
import { SLOT_LIST } from 'views/resource_portal/routes/routes';
import {
  CREATE_EXPERIENCE_MEETING_REQUEST,
  CREATE_EXPERIENCE_MEETING_SUCCESS,
  UPDATE_EXPERIENCE_MEETING_SUCCESS,
  UPDATE_EXPERIENCE_MEETING_REQUEST,
} from '../actions/experienceActionTypes';

import Api from '../api';

function* fetchExperience(action) {
  try {
    const params = { resourceId: action.resourceId, id: action.id };
    const response = yield call(Api.fetchExperience, params);
    const experience = response.data.data.attributes;
    const { onlineMeeting, onlineMeetingVendors } = experience;
    yield put({
      type: 'FETCH_EXPERIENCE_SUCCESS',
      experience,
      onlineMeeting,
      onlineMeetingVendors,
    });
  } catch (error) {
    yield put({ type: 'FETCH_EXPERIENCES_FAILURE', error });
  }
}

function* fetchExperiences(action) {
  try {
    const params = { resourceId: action.resourceId };
    const response = yield call(Api.fetchExperiences, params);
    let { tour } = normalize(response.data.experiences); // digging into response here is necessary because backend is putting data under that key
    if (!tour) tour = [];
    yield put({ type: 'FETCH_EXPERIENCES_SUCCESS', tour });
  } catch (error) {
    yield put({ type: 'FETCH_EXPERIENCES_FAILURE', error });
  }
}

function* createExperience(action) {
  try {
    const params = {
      resourceId: action.resourceId,
      name: action.name,
      custom_type_id: action.customTypeId,
      online: action.online,
    };
    const response = yield call(Api.createExperience, params);
    const experience = response.data;
    yield put({ type: 'CREATE_EXPERIENCE_SUCCESS', experience });
    yield put(push(`experiences/${experience.id}/edit/about`));
  } catch (error) {
    yield put({ type: 'CREATE_EXPERIENCE_FAILURE', error });
  }
}

function* updateExperience(action) {
  try {
    // Submit tour to backend
    const response = yield call(Api.updateExperience, action.values);
    const experience = response.data.data.attributes;
    const { errors } = response.data;
    const assets = response.data.data.attributes.assets.data.map(
      (asset) => asset.attributes
    );
    yield put({
      type: 'UPDATE_EXPERIENCE_SUCCESS',
      experience,
      assets,
      experienceErrors: errors,
    });

    // Depending on tour progress of the form as a whole, navigate to the next step,
    // or stay on same and show success notification.
    const { nextStep } = action;
    if (nextStep && nextStep !== '/experiences') {
      yield put({ type: 'STEP_SELECTED', step: action.nextStep });
      const url = `/experiences/${experience.id}/edit/${action.nextStep}`;
      yield put(push(url));
    } else {
      yield put({
        type: 'SHOW_NOTIFICATION',
        noticeMessage: {
          msg: 'Your experience has been updated!',
          type: 'notice',
        },
      });
      // Wait for 3 secs before hiding notification
      if (nextStep === '/experiences') {
        yield put({ type: 'SHOW_NOTIFICATION', noticeMessage: null });
        yield put(push(nextStep));
      } else {
        yield delay(3000);
        yield put({ type: 'SHOW_NOTIFICATION', noticeMessage: null });
      }

      //--
    }
  } catch (e) {
    console.log('error in saga', e);
    yield put({
      type: 'UPDATE_EXPERIENCE_FAILED',
      message: e.message,
    });
  }
}

function* createExperienceMeetingInfo(action) {
  try {
    const response = yield call(Api.createExperienceMeetingInfo, action.values);
    const onlineMeeting = response.data.data.attributes;
    yield put({
      type: CREATE_EXPERIENCE_MEETING_SUCCESS,
      onlineMeeting,
    });
  } catch (e) {
    console.log('error in saga', e);
  }
}

function* updateExperienceMeetingInfo(action) {
  try {
    const response = yield call(Api.updateExperienceMeetingInfo, action.values);
    const onlineMeeting = response.data.data.attributes;
    yield put({
      type: UPDATE_EXPERIENCE_MEETING_SUCCESS,
      onlineMeeting,
    });
  } catch (e) {
    console.log('error in saga', e);
  }
}

function* createPhoto(action) {
  try {
    if (!action) return;
    const response = yield call(Api.createPhoto, action.attrs);
    let animLength = 2000;
    if (response && !response.data.error) {
      yield put({
        type: 'CREATE_PHOTO_SUCCESS',
        asset: response.data.data.attributes,
      });
      yield put({
        type: 'SHOW_NOTIFICATION',
        noticeMessage: {
          msg: t('PHOTO_UPLOADED_SUCCESSFULLY'),
          type: 'notice',
        },
      });
    } else {
      yield put({ type: 'CREATE_PHOTO_FAILURE' });
      animLength = 4000;

      yield put({
        type: 'SHOW_NOTIFICATION',
        noticeMessage: {
          msg: `${response.data.message}: ${response.data.error}`,
          type: 'error',
        },
      });
    }
    yield delay(animLength);
    yield put({ type: 'SHOW_NOTIFICATION', noticeMessage: null });

    if (action.nextStep && action.nextStep.navTo) {
      yield put({ type: 'STEP_SELECTED', step: action.nextStep.navTo });
    }
  } catch (e) {
    console.log('error', e);
  }
}

function* removePhoto(action) {
  try {
    const response = yield call(Api.removePhoto, action.attrs);
    const deletedImage = response.data;

    yield put({ type: 'REMOVE_PHOTO_SUCCESS', deletedImage });
    yield put({
      type: 'SHOW_NOTIFICATION',
      noticeMessage: { msg: 'Image deleted successfully', type: 'notice' },
    });

    yield delay(2000);
    yield put({ type: 'SHOW_NOTIFICATION', noticeMessage: null });
  } catch (e) {
    console.log('error', e);
  }
}

function* repositionPhoto(action) {
  try {
    // need asset id and new position index
    const response = yield call(Api.repositionPhoto, action.attrs);
    const asset = response.data;
    yield put({ type: 'MOVE_PHOTO_SUCCESS', asset });
  } catch (e) {
    console.log('error', e);
  }
}

function* setSelectedStep(action) {
  const form = yield select(getTourStepForm);

  if (form) {
    yield put({
      type: 'TOGGLE_CHANGES_MODAL',
      visible: true,
      redirectTo: action.step,
    });
  } else {
    yield put({ type: 'STEP_SELECTED', action });
    yield put({ type: 'TOGGLE_CHANGES_MODAL', visible: false });
    yield put(push(action.step));
  }
}

function* fetchAvailability(action) {
  try {
    const experienceId = action.payload;
    const experience = yield select(getExperienceById(experienceId));
    const resourceId = yield select(getUserId);

    if (experience.availabilityDirty) {
      yield put(fetchExperienceAvailabilityStarted());
      yield put({ ...action, type: showAvailabilityModal.type });

      const { data } = yield call(Api.fetchAvailability, {
        resourceId,
        experienceId,
      });

      yield put(fetchExperienceAvailabilitySucceeded(normalize(data)));
    }
  } catch (error) {
    yield put(fetchExperienceAvailabilityFailed({ error: error.message }));
  }
}

const createSchedule = (action) => fetchAvailability(action);

function* saveTheSchedule(_action) {
  const resourceId = yield select(getUserId);
  const experienceId = yield select(getSelectedExperienceId);
  const { isOnline, timeZone: timezone } = yield select(
    getExperienceSchedule(experienceId)
  );
  const resourceTimezone = yield select(getResourceTimezone);

  let date = yield select(getSelectedDate());
  let time = yield select(getSelectedTime());

  try {
    // Convert timezone if necessary
    // Format data for backend
    const convertedDateTime = convertDateTimezone({
      originalTimezone: isOnline ? resourceTimezone : timezone,
      timezone,
      date: date.trim(),
      dateFormat: 'YYYYMMDD',
      time: time.replace(/\s+/g, ''),
      timeFormat: 'h:mm A',
    });

    date = convertedDateTime.format('YYYYMMDD');
    time = convertedDateTime.format('h:mm A');

    const { data } = yield call(Api.saveSchedule, {
      resourceId,
      experienceId,
      date,
      time,
    });

    yield put(saveScheduleSucceeded(data));
    yield put(push(SLOT_LIST));
    yield put(fetchSlots());
  } catch (error) {
    yield put(saveScheduleFailed({ error: error.message }));
  }
}

export const experienceSagas = [
  takeLatest('FETCH_EXPERIENCE_REQUEST', fetchExperience),
  takeLatest('FETCH_EXPERIENCES_REQUEST', fetchExperiences),
  takeLatest('CREATE_EXPERIENCE_REQUEST', createExperience),
  takeLatest('UPDATE_EXPERIENCE_REQUEST', updateExperience),
  takeEvery('CREATE_PHOTO_REQUEST', createPhoto),
  takeLatest('REMOVE_PHOTO_REQUEST', removePhoto),
  takeLatest('MOVE_PHOTO_REQUEST', repositionPhoto),
  takeLatest('REQUEST_STEP_SELECTED', setSelectedStep),
  takeLatest(CREATE_EXPERIENCE_MEETING_REQUEST, createExperienceMeetingInfo),
  takeLatest(UPDATE_EXPERIENCE_MEETING_REQUEST, updateExperienceMeetingInfo),
  takeLatest(createScheduleSelected.type, createSchedule),
  takeLatest(saveSchedule.type, saveTheSchedule),
];
